import {
  DealsSearchPreviewResponse,
  dealsSearchPreviewResponseSchema,
  DealsSearchResultsResponse,
  dealsSearchResultsResponseSchema,
} from '@/features/deals/types/dealSearch';
import { SearchContext, searchContextSchema } from '@/features/searchV2/types/tracking';
import { z } from 'zod';

const dealsSearchStateSchema = z.object({
  pendingSSRHydration: z.boolean(),
  ssrRenderedPage: z.number().optional(),
  isExtended: z.boolean(),
  fetching: z.boolean(),
  extendedFetching: z.boolean(),
  previewRedirectType: z.enum(['deals', 'sistaminuten']).nullable(),
  preview: dealsSearchPreviewResponseSchema.nullable(),
  list: dealsSearchResultsResponseSchema.nullable(),
  bounds: z.string().optional(),
  searchContext: searchContextSchema.optional(),
  isIndexable: z.boolean(),
});

export type DealsSearchState = z.infer<typeof dealsSearchStateSchema>;

export const DEALS_SEARCH_ACTION = {
  SET_FETCHING: 'dealsSearch/SET_FETCHING',
  SET_LIST_RESULTS: 'dealsSearch/SET_LIST_RESULTS',
  SET_PREVIEW_RESULTS: 'dealsSearch/SET_PREVIEW_RESULTS',
  SET_EXTENDED_FETCHING: 'dealsSearch/SET_LIST_MORE_FETCHING',
  SET_EXTENDED_RESULTS: 'dealsSearch/SET_LIST_MORE_RESULTS',
  SET_SEARCH_CONTEXT: 'dealsSearch/SET_SEARCH_CONTEXT',
  SET_RESET: 'dealsSearch/SET_RESET',
  SET_BOUNDS: 'dealsSearch/SET_BOUNDS',
  SET_BOUNDS_FIT: 'dealsSearch/SET_BOUNDS_FIT',
  CONSUME_SSR_HYDRATION: 'dealsSearch/CONSUME_SSR_HYDRATION',
  SET_RESTORE_STATE: 'dealsSearch/SET_RESTORE_STATE',
} as const;

type DealsSearchAction =
  | { type: typeof DEALS_SEARCH_ACTION.SET_FETCHING; payload: boolean }
  | {
      type: typeof DEALS_SEARCH_ACTION.SET_LIST_RESULTS;
      payload: DealsSearchResultsResponse;
    }
  | {
      type: typeof DEALS_SEARCH_ACTION.SET_PREVIEW_RESULTS;
      payload: { preview: DealsSearchPreviewResponse; previewRedirectType: 'deals' | 'sistaminuten' | null };
    }
  | { type: typeof DEALS_SEARCH_ACTION.SET_EXTENDED_FETCHING; payload: boolean }
  | {
      type: typeof DEALS_SEARCH_ACTION.SET_EXTENDED_RESULTS;
      payload: DealsSearchResultsResponse;
    }
  | { type: typeof DEALS_SEARCH_ACTION.SET_RESET }
  | { type: typeof DEALS_SEARCH_ACTION.SET_SEARCH_CONTEXT; payload: SearchContext }
  | { type: typeof DEALS_SEARCH_ACTION.SET_BOUNDS; payload: string | undefined }
  | { type: typeof DEALS_SEARCH_ACTION.CONSUME_SSR_HYDRATION }
  | { type: typeof DEALS_SEARCH_ACTION.SET_RESTORE_STATE; payload: DealsSearchState };

const initialState: DealsSearchState = {
  pendingSSRHydration: false,
  isExtended: false,
  fetching: false,
  extendedFetching: false,
  preview: null,
  previewRedirectType: null,
  list: null,
  bounds: undefined,
  searchContext: undefined,
  isIndexable: false,
};

export function dealsSearch(state: DealsSearchState = initialState, action: DealsSearchAction): DealsSearchState {
  switch (action.type) {
    case DEALS_SEARCH_ACTION.SET_FETCHING:
      return { ...state, fetching: action.payload, previewRedirectType: null };

    case DEALS_SEARCH_ACTION.SET_LIST_RESULTS:
      return {
        ...state,
        pendingSSRHydration: false,
        isExtended: false,
        fetching: false,
        extendedFetching: false,
        list: action.payload,
        preview: null,
        previewRedirectType: null,
      };

    case DEALS_SEARCH_ACTION.SET_PREVIEW_RESULTS: {
      return {
        ...state,
        pendingSSRHydration: false,
        fetching: false,
        list: null,
        preview: action.payload.preview,
        previewRedirectType: action.payload.previewRedirectType,
      };
    }

    case DEALS_SEARCH_ACTION.SET_EXTENDED_FETCHING:
      return { ...state, extendedFetching: action.payload };

    case DEALS_SEARCH_ACTION.SET_EXTENDED_RESULTS: {
      if (!state.list) return { ...state, extendedFetching: false };
      const response = action.payload;

      if (state.list.type === 'deals' && response.type === 'deals') {
        return {
          ...state,
          isExtended: true,
          extendedFetching: false,
          list: {
            ...state.list,
            results: [...state.list.results, ...response.results],
            page: response.page,
            isLastPage: response.isLastPage,
            total: response.total,
          },
        };
      }

      if (state.list.type === 'sistaminuten' && response.type === 'sistaminuten') {
        return {
          ...state,
          isExtended: true,
          extendedFetching: false,
          list: {
            ...state.list,
            results: [...state.list.results, ...response.results],
            page: response.page,
            isLastPage: response.isLastPage,
            total: response.total,
          },
        };
      }

      if (state.list.type === 'brand_deals' && response.type === 'brand_deals') {
        return {
          ...state,
          isExtended: true,
          extendedFetching: false,
          list: {
            ...state.list,
            results: [...state.list.results, ...response.results],
            page: response.page,
            isLastPage: response.isLastPage,
            total: response.total,
          },
        };
      }

      return { ...state, extendedFetching: false };
    }

    case DEALS_SEARCH_ACTION.CONSUME_SSR_HYDRATION:
      return { ...state, pendingSSRHydration: false };

    case DEALS_SEARCH_ACTION.SET_RESET:
      return initialState;

    case DEALS_SEARCH_ACTION.SET_BOUNDS: {
      return {
        ...state,
        bounds: action.payload,
      };
    }

    case DEALS_SEARCH_ACTION.SET_SEARCH_CONTEXT: {
      return {
        ...state,
        searchContext: action.payload,
      };
    }

    case DEALS_SEARCH_ACTION.SET_RESTORE_STATE: {
      return {
        ...action.payload,
        fetching: false,
        extendedFetching: false,
        pendingSSRHydration: false,
        // Restored on coming back to the list, which continues as `free_search` like a list a back/forward re-fetches.
        searchContext: 'free_search',
      };
    }

    default:
      return state;
  }
}
